/**
 * Helpers for the "Entries per Judge" limit type (waitlist_options.entries_per_judge).
 * Shared by the API (enforcement, capacity metrics) and the web app (Waitlist tab, registration).
 */

export interface IJudgeRef {
  judge_name: string;
  judge_number?: string;
}

export function getJudgeEntryLimitKey(judgeName?: string, judgeNumber?: string): string {
  return `${(judgeName || "").trim()}|${(judgeNumber || "").trim()}`;
}

/**
 * Judges in this trial: ring running-order judges first (Judge Schedules), then
 * show_element judges for shows without a ring structure yet. Deduplicated by name + number.
 */
export function getShowJudgesForEntryLimits(show: {
  rings?: { running_order?: { judge_name?: string; judge_number?: string }[] }[];
  show_elements?: { judge_name?: string; judge_number?: string }[];
}): IJudgeRef[] {
  const judges: IJudgeRef[] = [];
  const seen = new Set<string>();

  const add = (name?: string, number?: string) => {
    const judgeName = (name || "").trim();
    if (!judgeName) {
      return;
    }
    const key = getJudgeEntryLimitKey(judgeName, number);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    judges.push({ judge_name: judgeName, judge_number: (number || "").trim() || undefined });
  };

  for (const ring of show.rings || []) {
    for (const ro of ring.running_order || []) {
      add(ro.judge_name, ro.judge_number);
    }
  }
  for (const se of show.show_elements || []) {
    add(se.judge_name, se.judge_number);
  }

  return judges;
}

export function isManualEntryHoldActive(manualEntryLimitDate?: Date | string, now: Date = new Date()): boolean {
  if (!manualEntryLimitDate) {
    return false;
  }
  return now.getTime() < new Date(manualEntryLimitDate).getTime();
}

export interface IJudgeEntryAvailability {
  /** Held spots that still apply (0 once the hold is released or not configured). */
  active_reserve: number;
  /** Online entries can be accepted while online_entries < online_limit. */
  online_limit: number;
  online_available: number;
  total_available: number;
}

/**
 * Online entries are allowed while online < total - activeReserve AND online + manual < total.
 * Manual (on-behalf) entries are allowed while online + manual < total.
 */
export function getJudgeEntryAvailability(params: {
  total_entries: number;
  manual_entries?: number;
  online_entries: number;
  manual_entry_count: number;
  hold_active: boolean;
}): IJudgeEntryAvailability {
  const total = Math.max(0, params.total_entries || 0);
  const activeReserve = params.hold_active ? Math.min(total, Math.max(0, params.manual_entries || 0)) : 0;
  const used = params.online_entries + params.manual_entry_count;
  const totalAvailable = Math.max(0, total - used);
  const onlineLimit = Math.max(0, Math.min(total - activeReserve, total - params.manual_entry_count));
  const onlineAvailable = Math.max(0, Math.min(onlineLimit - params.online_entries, totalAvailable));

  return {
    active_reserve: activeReserve,
    online_limit: onlineLimit,
    online_available: onlineAvailable,
    total_available: totalAvailable,
  };
}
